import React, { useCallback, useMemo } from 'react';
import { Divider, HStack, Stack, Text, VStack } from '@adc/parallax-component-library';
import { IconLinkArrow12x24 } from '@adc/parallax-icons';
import { Link } from 'Components/utility';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
const PartnerCard = ({ partnerConnections, partnerInvitations, availableCommercialAPIPartners, }) => {
    const hasCommercialApiPartnersAvailable = Boolean(availableCommercialAPIPartners?.length);
    const sortedConnections = useMemo(() => {
        if (!partnerConnections?.length) {
            return [];
        }
        return [...partnerConnections].sort((a, b) => b.consent.created - a.consent.created);
    }, [partnerConnections]);
    const sortedInvitations = useMemo(() => {
        if (!partnerInvitations?.length) {
            return [];
        }
        return [...partnerInvitations].sort((a, b) => b.invitation.created - a.invitation.created);
    }, [partnerInvitations]);
    const handleAddConnection = useCallback(() => {
        mediator.publish('router:navigate', {
            url: '/partner-add-connection',
            data: { availableCommercialAPIPartners },
        });
    }, [availableCommercialAPIPartners]);
    const handleOpenConnection = useCallback((partnerConnection) => {
        mediator.publish('router:navigate', {
            url: '/partner-connection',
            data: { partnerConnection },
        });
    }, []);
    const handleOpenInvitation = useCallback((partnerInvitation) => {
        mediator.publish('router:navigate', {
            url: '/partner-confirm-connection',
            data: {
                partnerConnection: {
                    partner: partnerInvitation.partner,
                    consent: {
                        ...partnerInvitation.invitation,
                    },
                },
            },
        });
    }, []);
    const renderEmptyState = useCallback(() => (React.createElement(VStack, null,
        React.createElement(Text, { testID: "Partner.no-connections", fontSize: "$bodyBase.default", color: "$text.100" }, i18n.t('Connections.content.otherConnections.bodyText')),
        React.createElement(Divider, { marginVertical: "$4", borderColor: "$neutral.40" }),
        React.createElement(Link, { testID: "Partner.addConnection-btn", onPress: handleAddConnection }, i18n.t('Connections.content.otherConnections.links.addConnectionBtn.label')))), [handleAddConnection]);
    const renderList = () => (React.createElement(React.Fragment, null,
        sortedInvitations.map((partnerInvitation, index) => {
            const isLast = index === sortedInvitations.length - 1;
            return (React.createElement(VStack, { key: partnerInvitation.partner.id, onPress: () => handleOpenInvitation(partnerInvitation) },
                React.createElement(HStack, { justifyContent: "space-between" },
                    React.createElement(Text, { testID: "PartnerInvitation.pending-label", fontWeight: "$bodyBase.medium", fontSize: "$bodyBase.medium", color: "$text.100", width: "90%" }, i18n.t('Connections.content.otherConnections.secondaryText')),
                    React.createElement(IconLinkArrow12x24, { testID: "PartnerInvitation.arrow-icon", color: "$interactive.tertiary.enabled.onColor", style: { width: 12 } })),
                React.createElement(Text, { color: "$text.60", fontSize: "$bodySmall.default", fontWeight: "$bodySmall.default", testID: "PartnerInvitation.partnerName" }, partnerInvitation.partner.name),
                !isLast && React.createElement(Divider, { marginVertical: "$2", borderColor: "$neutral.40" })));
        }),
        sortedConnections.length > 0 && sortedInvitations.length > 0 && (React.createElement(Divider, { marginVertical: "$2", borderColor: "$neutral.40" })),
        sortedConnections.map((partnerConnection, index) => {
            const isLast = index === sortedConnections.length - 1;
            return (React.createElement(VStack, { key: partnerConnection.partner.id },
                React.createElement(HStack, { justifyContent: "space-between", paddingVertical: !isLast ? '$2' : '$0', onPress: () => handleOpenConnection(partnerConnection) },
                    React.createElement(Text, { testID: "Partner.name", fontWeight: "$bodyBase.medium", fontSize: "$bodyBase.medium", color: "$text.100", width: "90%", numberOfLines: 1 }, partnerConnection.partner.name),
                    React.createElement(IconLinkArrow12x24, { testID: "Partner.arrow-icon", color: "$interactive.tertiary.enabled.onColor", style: { width: 12 } })),
                !isLast && React.createElement(Divider, { marginVertical: "$2", borderColor: "$neutral.40" })));
        }),
        hasCommercialApiPartnersAvailable && (React.createElement(React.Fragment, null,
            React.createElement(Divider, { marginVertical: "$4", borderColor: "$neutral.40" }),
            React.createElement(Link, { testID: "Partner.addConnection-btn", onPress: handleAddConnection }, i18n.t('Connections.content.otherConnections.links.addConnectionBtn.label'))))));
    if (!partnerConnections?.length && !hasCommercialApiPartnersAvailable) {
        return null;
    }
    return (React.createElement(Stack, { marginTop: "$4", padding: "$4", backgroundColor: "$surface.container", border: "$1.5", shadow: "$1" },
        React.createElement(VStack, null,
            React.createElement(Text, { testID: "Partner.title", fontWeight: "$h4.strong", fontSize: "$h4.medium", color: "$text.100", marginBottom: "$5" }, i18n.t('Connections.content.otherConnections.primaryText')),
            sortedConnections.length === 0 && sortedInvitations.length === 0
                ? renderEmptyState()
                : renderList())));
};
export default PartnerCard;
